import { CloseOutlined } from '@ant-design/icons'
import { Divider, Form, Row, Tag } from 'antd'
import { TagSelectorWrapper } from 'atom/tagging'
import { ReusableSelector } from 'components/reusableSelector/selector'
import { hexToRgba } from 'helpers/color'
import { useListingCommissionFeeTranslation } from 'hooks/translation/useListingCommissionFeeTrans'
import { useTagging } from 'hooks/useTagging'
import { Tagging, TaggingTableName } from 'models/tagging'
import { useEffect, useRef } from 'react'
import { useAppState } from 'state'
import { theme } from 'theme'

interface props {
  taggings: Tagging[]
  setTaggings: React.Dispatch<React.SetStateAction<Tagging[]>>
  objectIds: { id: number }[]
  hideDivider?: boolean
  disabled?: boolean
  tableName: TaggingTableName
  dropdownRender: (
    tableName: TaggingTableName,
    hideAdd?: boolean,
  ) => (menu: React.ReactElement<any, string | React.JSXElementConstructor<any>>) => JSX.Element
  appliedTaggings?: Tagging[]
}

export const ObjectTagging: React.FC<props> = ({
  taggings,
  setTaggings,
  objectIds,
  hideDivider = false,
  disabled = false,
  tableName,
  dropdownRender,
  appliedTaggings,
}) => {
  const { isMockUpEnabled } = useAppState()
  const { taggingText } = useListingCommissionFeeTranslation()
  const { checkCurrentObjectsTagged, renderTagOption } = useTagging()
  const hasMergedRef = useRef(false)

  // Merge appliedTaggings into taggings state when both are available
  useEffect(() => {
    if (appliedTaggings && appliedTaggings.length > 0 && taggings.length > 0 && !hasMergedRef.current) {
      const needsMerge = appliedTaggings.some((appliedTag) => {
        const existingTag = taggings.find((t) => t.id === appliedTag.id)
        if (!existingTag) return false
        const appliedTaggedObjects = appliedTag.taggedObjects || []
        const existingTaggedObjects = existingTag.taggedObjects || []
        return appliedTaggedObjects.some((objId) => !existingTaggedObjects.includes(objId))
      })

      if (needsMerge) {
        setTaggings((prevTaggings) => {
          return prevTaggings.map((tag) => {
            const appliedTag = appliedTaggings.find((t) => t.id === tag.id)
            if (appliedTag && appliedTag.taggedObjects && appliedTag.taggedObjects.length > 0) {
              const mergedTaggedObjects = Array.from(
                new Set([...(tag.taggedObjects || []), ...appliedTag.taggedObjects]),
              )
              return { ...tag, taggedObjects: mergedTaggedObjects }
            }
            return tag
          })
        })
        hasMergedRef.current = true
      }
    }
  }, [appliedTaggings, taggings.length, setTaggings])

  const selectTag = (method: 'add' | 'remove') => (tagId: number) => {
    setTaggings((prevTaggings) => {
      return prevTaggings.map((tag) => {
        if (tag.id === tagId) {
          if (method === 'add') {
            const currentObjectIds = objectIds.map(({ id }) => id)
            const existingTaggedObjects = tag.taggedObjects || []
            const mergedTaggedObjects = Array.from(new Set([...existingTaggedObjects, ...currentObjectIds]))
            return { ...tag, taggedObjects: mergedTaggedObjects }
          } else {
            const currentObjectIds = objectIds.map(({ id }) => id)
            const filteredTaggedObjects = (tag.taggedObjects || []).filter((id) => !currentObjectIds.includes(id))
            return { ...tag, taggedObjects: filteredTaggedObjects }
          }
        }
        return tag
      })
    })
  }

  // Get tags to display
  const tagsToDisplay = taggings.filter((tag) => {
    return checkCurrentObjectsTagged(tag.taggedObjects || [], objectIds)
  })

  return (
    <TagSelectorWrapper>
      {!hideDivider && <Divider>{taggingText}</Divider>}
      <Form.Item name="taggings" label={taggingText} className="align-center">
        <ReusableSelector
          showSearch
          isSingle
          nativeSearch
          disabled={disabled}
          clearSearchAfterSelect
          placeholder={taggingText}
          dropDownList={taggings}
          showMagnifySVG={false}
          maxTagCount={0}
          isDebounceFetcher={false}
          handleOptionChange={selectTag('add')}
          debounceFetcher={undefined}
          renderOption={renderTagOption}
          dropdownRender={dropdownRender(tableName)}
          isMockUpEnabled={isMockUpEnabled}
        />
        <Row wrap gutter={8} style={{ marginBottom: 32 }}>
          {tagsToDisplay.map((tag) => (
            <Tag
              key={tag.id}
              style={{ marginTop: 16 }}
              color={hexToRgba(theme.colors.primary, 0.1)}
              closable={!disabled}
              closeIcon={<CloseOutlined style={{ color: theme.colors.softBlack }} />}
              onClose={() => selectTag('remove')(tag.id)}
            >
              {tag.tagName}
            </Tag>
          ))}
        </Row>
      </Form.Item>
    </TagSelectorWrapper>
  )
}
